import { useMovementContext } from '@common/context/MovementContext';
import { useUserState } from '@common/context/userContext';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
    interface Window {
        /**
         * @description The dataLayer is an array used by Google Tag Manager to store and manage data.
         * It is typically used to push events and parameters for tracking purposes.
         */
        dataLayer: Record<string, any>[];
    }
}

export const EventSources = {
    LIST_DEVICES_PAGE: 'list_devices_page',
    COMPLETED_MOVEMENT_PAGE: 'completed_movement_page',
    ADD_DEVICES_PAGE: 'add_devices_page',
    CONSIGNMENT_CONTEXT_MENU: 'consignment_context_menu',
    MOVEMENT_BANNER: 'movement_banner',
    NOT_SPECIFIED: 'not_specified',
    ADD_CONSIGNMENT_PAGE: 'add_consignment_page',
    UNLOCK_MODAL: 'unlock_modal',
    LOCK_MODAL: 'lock_modal',
    CONSIGNMENT_EDIT_PAGE: 'consignment_edit_page',
    PROFILE_PAGE: 'profile_page',
} as const;

export type EventSource = (typeof EventSources)[keyof typeof EventSources];

/**
 * Named pieces of the automatic context that an event can leave out.
 *
 * - `consignment_identifiers`: no `consignment_number`, and `current_url` is templated to the route
 *   pattern (`/consignments/summary/C-123456` → `/consignments/summary/:id`).
 * - `user_account_id`: no `user_account_id` (typed as "LPA ID or NLIS account ID or account email",
 *   so it may be an email address).
 *
 * A list of named omissions rather than per-key booleans, so a later "drop `user_envd_id` too" is
 * one more union member.
 *
 * Omitted keys are pushed as `undefined`, not left out. GTM merges every push into one data model
 * for the life of the page (eNVD is a single-page app, so that is the whole session) and a Data
 * Layer Variable returns the last value set for its key. A key that is merely absent would be sent
 * with the value an earlier event stored; naming it with `undefined` resets it.
 */
export type ContextOmission = 'consignment_identifiers' | 'user_account_id';

export interface TrackEventOptions {
    omit?: ReadonlyArray<ContextOmission>;
}

const CONSIGNMENT_NUMBER_PATTERN = /C-\d+/g;

// Utility function to extract consignment number from URL
const extractConsignmentNumber = (pathname: string): string | null => {
    const match = pathname.match(/C-\d+/);
    return match ? match[0] : null;
};

// Utility function to convert camelCase or spaced strings to snake_case
export function toSnakeCase(input: string): string {
    return (
        input
            // Replace spaces and hyphens with underscores
            .replace(/[\s-]+/g, '_')
            // Insert underscore between lower-to-upper case boundaries
            .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
            .replace(/_+/g, '_')
            // Lowercase the entire string
            .toLowerCase()
    );
}

export enum EventFeaturePrefix {
    Movement = 'mov_',
    None = '',
}

/**
 * A hook for tracking user interaction events and sending them to the GTM dataLayer.
 * It automatically enriches events with context like user, consignment, and URL information.
 *
 * Privacy rule for new feature events: they must not carry identifiers (consignment number, account
 * ID, PIC, names, contact details). Pass `{ omit: ['consignment_identifiers', 'user_account_id'] }`
 * and send fixed enums/statuses only. See docs/ANALYTICS.md.
 */
export const useAnalytics = () => {
    const location = useLocation();
    const [{ user }] = useUserState();
    const { movementActions } = useMovementContext();

    /**
     * Tracks a custom event and pushes it to the GTM dataLayer.
     * @param eventName - Name of the event to be tracked. Will be converted to snake_case and prefixed (e.g., "nextStep" → "mov_next_step").
     * @param source - The area of the app where the event originated, from `EventSources`. Should be one of the values from EventSources (e.g., "movement_banner").
     * @param customParameters - Optional additional data specific to this event. Merged into the final payload, potentially overriding context defaults.
     * @param options - `omit`: named pieces of the automatic context to leave out (see `ContextOmission`).
     */
    const trackEvent = useCallback(
        (
            eventName: string,
            prefix: EventFeaturePrefix = EventFeaturePrefix.None,
            source: EventSource = EventSources.NOT_SPECIFIED,
            customParameters: Record<string, any> = {},
            options: TrackEventOptions = {}
        ) => {
            const omit = options.omit ?? [];
            const omitConsignmentIdentifiers = omit.includes('consignment_identifiers');
            const omitUserAccountId = omit.includes('user_account_id');

            const consignmentNumber = extractConsignmentNumber(location.pathname);
            const currentActions =
                consignmentNumber && movementActions ? movementActions[consignmentNumber] : undefined;

            const formattedEventName = `${prefix}${toSnakeCase(eventName)}`;

            // Every event will include the following context data
            const contextData = {
                current_url: omitConsignmentIdentifiers
                    ? location.pathname.replace(CONSIGNMENT_NUMBER_PATTERN, ':id')
                    : location.pathname,
                ...(omitConsignmentIdentifiers
                    ? { consignment_number: undefined }
                    : consignmentNumber && { consignment_number: consignmentNumber }),
                ...(user?.accountDetails && {
                    user_envd_id: user.accountDetails.id,
                    user_account_id: user.accountDetails.accountId,
                    user_nlis_account_type: user.accountDetails.nlisAccountType,
                    user_account_type: user.accountDetails.accountType,
                }),
                ...(omitUserAccountId && { user_account_id: undefined }),
                ...(currentActions && { user_is_receiver: currentActions.isReceiver }),
            };

            // Combine context data with custom parameters from the event to produce the final event data
            const eventDataToPush = {
                // The 'event' key is crucial for GTM to identify this as a custom event
                event: formattedEventName,
                event_source: source, // This becomes a parameter of the custom event
                ...contextData,
                ...customParameters, // Custom params can override context data if needed
            };

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(eventDataToPush);
        },
        [location.pathname, movementActions, user]
    );

    const setUserId = useCallback((userId: string) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'setUserId',
            user_id: userId,
        });
    }, []);

    return {
        trackEvent,
        setUserId,
    };
};
